<template>
  <!-- Abwesenheitsnotiz mit automatischer Antwort (Kundenfeedback Kanzlei
       18.10.2026, V). Teil des Vertretungs-Editors; gespeichert wird mit
       „Vertretung speichern“. -->
  <section aria-labelledby="absence-notice-title" class="an" :class="{ 'an--on': modelValue.enabled }" data-testid="absence-notice">
    <header class="an__head">
      <span class="an__icon"><MessageSquareReply :size="18" /></span>
      <div class="an__titles">
        <h4 id="absence-notice-title" class="an__title">{{ $gt('Automatische Abwesenheitsnotiz senden') }}</h4>
        <p class="an__sub">
          {{ $gt('Schreibt Ihnen ein Externer oder Beteiligter während Ihrer Abwesenheit, erhält er einmalig eine automatische Antwort.') }}
        </p>
      </div>
      <button
        :aria-checked="modelValue.enabled ? 'true' : 'false'"
        :aria-label="$gt('Automatische Abwesenheitsnotiz senden')"
        class="an__switch"
        data-testid="absence-notice-switch"
        role="switch"
        type="button"
        @click="update({ enabled: !modelValue.enabled })"
      >
        <span class="an__knob" />
      </button>
    </header>

    <p v-if="modelValue.enabled" class="an__state" :class="active ? 'an__state--live' : 'an__state--idle'">
      <span class="an__dot" />
      <template v-if="active">{{ $gt('Aktiv – wird während der laufenden Abwesenheit gesendet.') }}</template>
      <template v-else-if="hasPeriod">{{ $gt('Eingeschaltet – wird nur während eines eingetragenen Abwesenheitszeitraums gesendet.') }}</template>
      <template v-else>{{ $gt('Eingeschaltet – greift erst, wenn oben ein Abwesenheitszeitraum mit Vertretung eingetragen ist.') }}</template>
    </p>

    <div class="an__grid" :class="{ 'an__grid--off': !modelValue.enabled }">
      <div class="an__field">
        <label class="an__label" for="absence-notice-text">{{ $gt('Text der automatischen Antwort') }}</label>
        <textarea
          id="absence-notice-text"
          class="an__textarea"
          data-testid="absence-notice-text"
          :maxlength="maxLength"
          rows="8"
          :value="modelValue.message"
          @input="update({ message: ($event.target as HTMLTextAreaElement).value })"
        />
        <div class="an__row">
          <button
            class="an__link"
            :disabled="modelValue.message === defaultText"
            type="button"
            @click="update({ message: defaultText })"
          >
            <RotateCcw :size="12" />
            {{ $gt('Standardtext wiederherstellen') }}
          </button>
          <span class="an__count">{{ modelValue.message.length }} / {{ maxLength }}</span>
        </div>
        <p v-if="periodHint" class="an__warn" role="status">
          <AlertTriangle :size="13" />
          <span>
            {{ $gt('Der Text enthält eine Zeitangabe') }} („{{ periodHint }}“).
            {{ $gt('Externe sollen keinen Zeitraum erfahren – bitte ggf. entfernen.') }}
          </span>
        </p>
      </div>

      <figure class="an__preview" :aria-label="$gt('Vorschau der automatischen Antwort')">
        <figcaption class="an__preview-cap">{{ $gt('Vorschau – so sieht der Absender die Antwort') }}</figcaption>
        <div class="an__bubble">
          <div class="an__bubble-meta">
            <strong>{{ senderLabel }}</strong>
            <span class="an__badge"><CalendarOff :size="11" />{{ $gt('Automatische Antwort') }}</span>
          </div>
          <div class="an__bubble-body">
            <p v-for="(p, i) in previewParagraphs" :key="i">{{ p }}</p>
            <p v-if="previewParagraphs.length === 0" class="an__empty">{{ $gt('Kein Text – es gilt der Standardtext.') }}</p>
          </div>
        </div>
      </figure>
    </div>

    <ul class="an__facts">
      <li><EyeOff :size="13" />{{ $gt('Es wird kein Zeitraum und kein Rückkehrdatum genannt.') }}</li>
      <li><ShieldCheck :size="13" />{{ $gt('Jeder Absender erhält die Antwort höchstens einmal je Unterhaltung und Abwesenheit.') }}</li>
      <li><Users :size="13" />{{ $gt('Kollegen Ihrer eigenen Organisation erhalten keine automatische Antwort; auf automatische E-Mails wird nicht geantwortet.') }}</li>
    </ul>
  </section>
</template>

<script setup lang="ts">
  import {
    AlertTriangle, CalendarOff, EyeOff, MessageSquareReply, RotateCcw, ShieldCheck, Users,
  } from 'lucide-vue-next'
  import { computed } from 'vue'
  import { gt } from '@/i18n/gt'
  import { ABSENCE_NOTICE_MAX_LENGTH, type AbsenceNoticeSettings, findPeriodHint } from '@/services/substitutionApi'

  const props = defineProps<{
    modelValue: AbsenceNoticeSettings
    /** Abwesenheit heute aktiv (laufende Vertretungsregel). */
    active: boolean
    /** Mindestens eine Vertretungsregel eingetragen. */
    hasPeriod: boolean
    /** Name des abwesenden Kontos für die Vorschau (optional). */
    ownerName?: string
    defaultMessage: string
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: AbsenceNoticeSettings): void
  }>()

  const maxLength = ABSENCE_NOTICE_MAX_LENGTH
  const defaultText = computed(() => props.defaultMessage)
  const senderLabel = computed(() => props.ownerName || gt('Zuständiger Sachbearbeiter'))
  const periodHint = computed(() => findPeriodHint(props.modelValue.message))

  const previewParagraphs = computed(() =>
    (props.modelValue.message || '')
      .replace(/\r\n/g, '\n')
      .split(/\n{2,}/)
      .map(p => p.trim())
      .filter(Boolean),
  )

  function update (patch: Partial<AbsenceNoticeSettings>) {
    emit('update:modelValue', { ...props.modelValue, ...patch })
  }
</script>

<style scoped>
.an {
  --an-accent: #c2410c;
  --an-accent-soft: #fff7ed;
  --an-accent-line: #fed7aa;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 0 0 1.5px var(--border-grey-color, #dcdfe3);
  transition: box-shadow 0.18s ease;
}
.an--on {
  box-shadow: inset 0 0 0 1.5px var(--an-accent-line), 0 6px 18px -12px rgba(194, 65, 12, 0.45);
}

.an__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.an__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--an-accent-soft);
  color: var(--an-accent);
}
.an__titles { flex: 1; min-width: 0; }
.an__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--dark-color, #1f2937);
}
.an__sub {
  margin: 2px 0 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: var(--grey-color, #5f5f5f);
}

/* Schalter */
.an__switch {
  position: relative;
  flex-shrink: 0;
  width: 42px;
  height: 24px;
  margin-top: 4px;
  border: none;
  border-radius: 999px;
  background: #cbd5e1;
  cursor: pointer;
  transition: background-color 0.18s ease;
}
.an__switch[aria-checked='true'] { background: var(--an-accent); }
.an__switch:focus-visible { outline: 2px solid var(--primary-color, #0076e5); outline-offset: 2px; }
.an__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3);
  transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}
.an__switch[aria-checked='true'] .an__knob { transform: translateX(18px); }

/* Status */
.an__state {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 7px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}
.an__state--live { background: #ecfdf5; color: #065f46; }
.an__state--idle { background: var(--grey-light-color, #f3f5f7); color: var(--grey-color, #5f5f5f); }
.an__dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.an__state--live .an__dot { box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2); }

/* Text + Vorschau */
.an__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 16px;
  transition: opacity 0.18s ease;
}
.an__grid--off { opacity: 0.6; }
.an__field { display: flex; flex-direction: column; gap: 6px; }
.an__label {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--grey-color, #5f5f5f);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.an__textarea {
  width: 100%;
  min-height: 132px;
  padding: 10px 12px;
  border: 1.5px solid var(--border-grey-color, #dcdfe3);
  border-radius: 8px;
  font: inherit;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--dark-color, #1f2937);
  background: #fff;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease;
}
.an__textarea:focus { border-color: var(--an-accent); }
.an__row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.an__link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 0;
  border: none;
  background: none;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--primary-color, #0076e5);
  cursor: pointer;
}
.an__link:disabled { color: #94a3b8; cursor: default; }
.an__link:hover:not(:disabled) { text-decoration: underline; }
.an__count { font-size: 0.74rem; color: #8a94a3; font-variant-numeric: tabular-nums; }
.an__warn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  padding: 7px 10px;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.78rem;
  line-height: 1.4;
}
.an__warn svg { flex-shrink: 0; margin-top: 2px; }

.an__preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 12px;
  border-radius: 10px;
  background: var(--grey-light-color, #f3f5f7);
}
.an__preview-cap {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--grey-color, #5f5f5f);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.an__bubble {
  align-self: flex-start;
  max-width: 100%;
  padding: 10px 12px;
  border-radius: 4px 14px 14px 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.an__bubble-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.78rem;
  color: var(--dark-color, #1f2937);
}
.an__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--an-accent-soft);
  color: #9a3412;
  box-shadow: inset 0 0 0 1px var(--an-accent-line);
  font-size: 0.68rem;
  font-weight: 600;
}
.an__bubble-body { font-size: 0.86rem; line-height: 1.5; color: #334155; }
.an__bubble-body p { margin: 0 0 6px; white-space: pre-line; }
.an__bubble-body p:last-child { margin-bottom: 0; }
.an__empty { color: #94a3b8; font-style: italic; }

.an__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.78rem;
  color: var(--grey-color, #5f5f5f);
}
.an__facts li { display: inline-flex; align-items: center; gap: 6px; }
.an__facts svg { flex-shrink: 0; color: var(--an-accent); }

@media (max-width: 760px) {
  .an__grid { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .an, .an__switch, .an__knob, .an__grid { transition: none; }
}
</style>
